import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import * as externalAuth from './api/externalAuth';
import { ExternalApiError } from './api/externalAuth';

export interface ExternalUser {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  // Phase 105 D-01: whether the user holds the sales-viewer grant.
  // `ExternalNavigation` gates the Leads entry on this.
  salesViewer: boolean;
}

interface ExternalAuthContextValue {
  user: ExternalUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (input: { email: string; password: string }) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const ExternalAuthContext = createContext<ExternalAuthContextValue | null>(null);

/**
 * Provider for the /external/* surface.
 *
 * Behavior:
 *   - On mount: fetches GET /api/external/me. 200 => sets user, 401 => sets user=null.
 *     Either way, isLoading flips to false.
 *   - login(): POST /api/external/login → on success, fetches /me to populate user.
 *   - logout(): POST /api/external/logout → clears user. NO toast, NO navigation here
 *     (caller decides — minimal happy path per locked decision).
 *   - refresh(): re-runs the /me fetch. Used by AcceptInvitePage post-success.
 *
 * IMPORTANT: 401 from /api/external/me is the canonical "logged out" signal. It does
 * NOT clear any localStorage tokens (cookie is HttpOnly, no client-side token to clear).
 * The internal <AuthProvider> still wraps the entire app — it is unaffected by 401s
 * here because services/api/externalAuth.ts uses fetch() directly (NOT services/api/client.ts,
 * which would side-effect localStorage on 401).
 */
export function ExternalAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<ExternalUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    try {
      const me = await externalAuth.getMe();
      setUser(me);
    } catch (err) {
      if (err instanceof ExternalApiError && err.statusCode === 401) {
        setUser(null);
      } else {
        // Unexpected error — leave user as-is, log for ops.
        console.error('External getMe failed:', err);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = useCallback(
    async (input: { email: string; password: string }) => {
      await externalAuth.login(input);
      // Backend set the cookie. Re-fetch /me to populate provider state from
      // the source of truth (avoids drift between login response shape and
      // /me response shape).
      await fetchMe();
    },
    [fetchMe],
  );

  const logout = useCallback(async () => {
    try {
      await externalAuth.logout();
    } catch (err) {
      console.warn('External logout call failed; clearing client state anyway:', err);
    } finally {
      setUser(null);
    }
  }, []);

  const refresh = useCallback(async () => {
    setIsLoading(true);
    await fetchMe();
  }, [fetchMe]);

  return (
    <ExternalAuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        login,
        logout,
        refresh,
      }}
    >
      {children}
    </ExternalAuthContext.Provider>
  );
}

export function useExternalAuth(): ExternalAuthContextValue {
  const context = useContext(ExternalAuthContext);
  if (!context) {
    throw new Error('useExternalAuth must be used within an ExternalAuthProvider');
  }
  return context;
}
